import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)"));
  return m ? decodeURIComponent(m[2]) : "";
}

// Authoritative CSRF token kept in memory, seeded from server responses
// (login / refresh / /auth/me). Immune to unreliable document.cookie parsing
// (e.g. duplicate/stale csrf_token cookies). Falls back to the cookie value.
let csrfToken = "";
export function setCsrfToken(t) {
  if (typeof t === "string") csrfToken = t;
}
function currentCsrf() {
  return csrfToken || getCookie("csrf_token");
}

// Attach the double-submit CSRF token on state-changing requests.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const token = currentCsrf();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// On 401 (expired session) or a stale-token CSRF 403, try one silent refresh then retry.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const skip = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const isCsrf = status === 403 && typeof detail === "string" && detail.toLowerCase().includes("csrf");
    if ((status === 401 || isCsrf) && !original._retry && !skip) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        const rr = await refreshing;
        refreshing = null;
        if (rr && rr.data && rr.data.csrf_token) csrfToken = rr.data.csrf_token;
        const token = currentCsrf();
        if (token) original.headers = { ...(original.headers || {}), "X-CSRF-Token": token };
        return api(original);
      } catch (e) {
        refreshing = null;
        // Session could not be refreshed: surface a clear, non-destructive message
        // (do not wipe the user's form) prompting a re-login.
        if (error.response) {
          error.response.data = {
            detail: "Sesi Anda telah berakhir. Silakan login ulang di tab lain, lalu tekan Simpan kembali.",
          };
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Terjadi kesalahan. Silakan coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const WIB_OPTS = {
  timeZone: "Asia/Jakarta",
  day: "2-digit",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

export function formatWIB(isoString) {
  if (!isoString) return "-";
  try {
    const fmt = new Intl.DateTimeFormat("id-ID", WIB_OPTS).format(new Date(isoString));
    return `${fmt} WIB`;
  } catch {
    return "-";
  }
}
